import { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from "@mui/material";

import { useEffect, useMemo } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { StatefulButton } from "../../components/ui/muiButtons";
import { useAccountContext } from "../../strict/app/AccountResolver";
import type { SaveEmailMatchMutationVariables } from "../../graphql/types";
import { Rule, SaveEmailMatchDocument, SaveEmailMatchMutation } from "../../graphql/types";

import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { IconButton } from "../../components/ui/icons";
import { validateEmail } from "./utils";

type EmailRuleModalProps = {
  open: boolean;
  onClose: () => void;
  rule?: Rule;
  recipient?: string;
  updateCache: (
    cache: ApolloCache,
    {
      data,
    }: {
      data?: SaveEmailMatchMutation | null;
    }
  ) => void;
};

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  width: "100%",
  display: "grid",
  alignItems: "center",
  padding: theme.spacing(1, 3, 4),
  gridTemplateColumns: "3fr 1fr",
  overflowY: "auto",
  gap: theme.spacing(2),
}));

const DialogActionsStyled = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(0, 3, 3),
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  variant: "h5",
}));

type Pattern = "glob" | "regex";

type DefaultValuesFormType = {
  recipient: string;
  path: string;
  sender: string;
  subject: string;
  senderType: Pattern;
  subjectType: Pattern;
};

type ToggleButtonRegexGlobType = {
  control: Control<DefaultValuesFormType, string>;
  fieldName: "senderType" | "subjectType";
  setValue: (fieldName: "senderType" | "subjectType", value: Pattern) => void;
};

const ToggleButtonRegexGlob = ({ control, fieldName, setValue }: ToggleButtonRegexGlobType) => (
  <Controller
    name={fieldName}
    control={control}
    render={({ field }) => {
      const ruleType = field.name === "senderType" ? "Sender" : "Subject";

      return (
        <ToggleButtonGroup
          exclusive
          value={field.value}
          onChange={(_event: React.MouseEvent<HTMLElement>, value: Pattern) => {
            setValue(field.name, value);
          }}
          sx={{ width: "240px", marginRight: "0px" }}
        >
          <ToggleButton
            value="glob"
            fullWidth={true}
            sx={{ marginRight: "-8px" }}
            aria-label={`${ruleType} as pattern`}
          >
            Pattern
          </ToggleButton>
          <ToggleButton value="regex" fullWidth={true} aria-label={`${ruleType} as regex`}>
            Regex
          </ToggleButton>
        </ToggleButtonGroup>
      );
    }}
  />
);

const getSuggestion = (urlName: string | null) => {
  if (!urlName) return "";
  const uuid = uuidv4();
  const uuidSegments = uuid.split("-");
  const fifthSegment = uuidSegments[4].toLowerCase();
  return `${urlName}-${fifthSegment}@ingest.switchboard-software.com`;
};

const EmailRuleModal = ({ open, onClose, rule, recipient, updateCache }: EmailRuleModalProps) => {
  const { urlName } = useAccountContext();
  const suggestion = useMemo(() => getSuggestion(urlName), [urlName]);
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
    setError,
    reset,
  } = useForm<DefaultValuesFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      recipient: recipient || suggestion,
      path: rule?.path || "",
      sender: rule?.senderGlob || rule?.senderRegex || "",
      subject: rule?.subjectGlob || rule?.subjectRegex || "",
      senderType: rule?.senderRegex ? "regex" : "glob",
      subjectType: rule?.senderRegex ? "regex" : "glob",
    },
  });

  const [saveEmailMatch, { loading }] = useMutation<SaveEmailMatchMutation, SaveEmailMatchMutationVariables>(
    SaveEmailMatchDocument,
    {
      onCompleted: (result) => {
        if (result?.save_emailmatch?.__typename) {
          switch (result?.save_emailmatch?.__typename) {
            case "InvalidRegexError": {
              const field = result?.save_emailmatch.field === "subjectRegex" ? "subject" : "sender";
              if (result?.save_emailmatch.field) {
                setError(field, {
                  message: "Invalid regex.",
                });
              }
              break;
            }
            case "InvalidPathError":
              setError("path", {
                message: "The path must be an S3 valid path.",
              });
              break;
            default: {
              const successMessage = recipient
                ? "Successfully updated rules for recipient"
                : "Successfully saved recipient";
              toast.success(successMessage);
              onClose();
              break;
            }
          }
        }
      },
      update: updateCache,
      onError: (error) => {
        toast.error(error.message);
        console.error(error);
      },
    }
  );

  useEffect(() => {
    reset({
      recipient: recipient || suggestion,
      path: rule?.path || "",
      sender: rule?.senderGlob || rule?.senderRegex || "",
      subject: rule?.subjectGlob || rule?.subjectRegex || "",
      senderType: rule?.senderRegex ? "regex" : "glob",
      subjectType: rule?.subjectRegex ? "regex" : "glob",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule, recipient, open, reset, urlName]);

  const onSubmit = (formData: DefaultValuesFormType) => {
    saveEmailMatch({
      variables: {
        recipient: rule?.id ? undefined : formData.recipient,
        id: rule?.id,
        rule: {
          path: formData.path,
          senderGlob: formData.senderType === "glob" ? formData.sender : undefined,
          senderRegex: formData.senderType === "regex" ? formData.sender : undefined,
          subjectGlob: formData.subjectType === "glob" ? formData.subject : undefined,
          subjectRegex: formData.subjectType === "regex" ? formData.subject : undefined,
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            minWidth: "800px",
            maxWidth: "50%",
          },
        },
      }}
    >
      <DialogTitleStyled>
        Add Rule
        <IconButton onClick={onClose}>
          <XIcon />
        </IconButton>
      </DialogTitleStyled>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContentStyled>
          <Controller
            name="recipient"
            rules={{ validate: validateEmail }}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                sx={{ gridColumn: "span 2" }}
                fullWidth
                label="Recipient"
                id="recipient"
                {...field}
                error={Boolean(fieldState.error?.message)}
                helperText={fieldState.error && fieldState.error.message}
                required={!recipient}
                disabled={!!recipient}
                aria-label="Recipient email"
              />
            )}
          />
          {!recipient && (
            <Typography
              variant="body.small.regular"
              sx={{
                gridColumn: "span 2",
              }}
            >
              Use <b>{suggestion}</b> or replace if needed.
            </Typography>
          )}
          <Controller
            name="path"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                required
                helperText={fieldState.error && fieldState.error.message}
                error={Boolean(fieldState.error?.message)}
                fullWidth
                sx={{ gridColumn: "span 2" }}
                id="path"
                label="Path"
                aria-label="Path"
                {...field}
              />
            )}
          />

          <Controller
            name="sender"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                helperText={fieldState.error && fieldState.error.message}
                error={Boolean(fieldState.error?.message)}
                fullWidth
                id="sender"
                label="Sender pattern"
                aria-label="Sender pattern  (optional)"
                {...field}
              />
            )}
          />
          <ToggleButtonRegexGlob control={control} fieldName="senderType" setValue={setValue} />

          <Controller
            name="subject"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                helperText={fieldState.error && fieldState.error.message}
                error={Boolean(fieldState.error?.message)}
                fullWidth
                id="subject"
                label="Subject pattern"
                aria-label="Subject pattern (optional)"
                {...field}
              />
            )}
          />
          <ToggleButtonRegexGlob control={control} fieldName="subjectType" setValue={setValue} />
        </DialogContentStyled>
        <DialogActionsStyled>
          <StatefulButton
            type="submit"
            variant="contained"
            color="primary"
            aria-label={`${rule?.id ? "Update" : "Save"} rule`}
            disabled={!isValid}
            loading={loading}
          >
            {rule?.id ? "Update" : "Save"}
          </StatefulButton>
        </DialogActionsStyled>
      </form>
    </Dialog>
  );
};
export { EmailRuleModal };
